import React, {useState, useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {Formik, Form} from 'formik';
import {useAppDispatch} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import {
  phoneNumberReplaceRegex
}
  from '../../constants/constants';
import Container from '../../components/common/layout/Container';
import ActionButtons from '../common/layout/ActionButtons';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import {addPatient} from '../../api/patients';
import {patientValidationSchema} from '../../constants/validationSchemes';
import {CreatePatientProps} from '../../constants/types';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {useTranslation} from 'react-i18next';
import {Button, ButtonWithLoader} from '../common/Button';
import {FormGroup} from '@mui/material';
import {TextField} from '../common/formik/fields';
import PhoneInput from '../common/formik/inputs/PhoneInput';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const initialValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  patientId: ''
};

type LocationState = {
  from?: string
}

const AddPatient = () => {
  const {t} = useTranslation('patients');
  const history = useHistory();
  const {state} = useLocation<LocationState>();
  const {from} = state || {from: ''};
  const dispatch = useAppDispatch();
  const [apiValidationError, setApiValidationError] =
    useState<Nullable<Dictionary<string[]>>>(null);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  const onSubmit = async (values: CreatePatientProps) => {
    values.phoneNumber =
      values.phoneNumber.replace(phoneNumberReplaceRegex, '');
    const sanitizeValues = sanitize(values);
    setApiValidationError(null);

    try {
      const newPatient = await addPatient(sanitizeValues);
      addSuccessToast(t('toasts.addPatientMessage'));
      if (from) {
        return history.push(from, {newPatientId: newPatient});
      }

      history.goBack();
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'patient');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  const cancelCreation = () => {
    history.goBack();
  };

  return (
    <Container header="Patients">
      <h2 className="subheader">Create New Patient</h2>

      <Formik
        initialValues={initialValues}
        validationSchema={patientValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({setFieldTouched, submitForm,
          isValid, isSubmitting, dirty}) => {
          useOnUnsavedChanges(dirty);

          return (
            <>
              <div className="creation-form-wrapper">
                <Form className="form">
                  <FormGroup>
                    <TextField
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => setFieldTouched('firstName', true)}
                      id="patientFirstName"
                      name="firstName"
                      label="Patient First Name"
                      required
                      disabled={isSubmitting}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextField
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => setFieldTouched('lastName', true)}
                      id="patientLastName"
                      name="lastName"
                      label="Patient Last Name"
                      required
                      disabled={isSubmitting}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextField
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => setFieldTouched('phoneNumber', true)}
                      label="Cell phone number"
                      name="phoneNumber"
                      id="patientPhoneNumber"
                      customInput={{
                        inputComponent: PhoneInput
                      }}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextField
                      onFocus={() => setApiValidationError(null)}
                      onBlur={() => setFieldTouched('patientId', true)}
                      id="patientId"
                      name="patientId"
                      label="Patient ID"
                      disabled={isSubmitting}
                      apiValidationError={apiValidationError}
                    />
                  </FormGroup>
                </Form>
              </div>

              <ActionButtons>
                <EnsurePermission
                  elementPermissions={permissionsInfo.patients.create}
                >
                  <ButtonWithLoader
                    type="submit"
                    variant="contained"
                    data-testid="create-patient"
                    label="Create new patient"
                    disabled={!isValid}
                    isSubmitting={isSubmitting}
                    onClick={submitForm}
                  />
                </EnsurePermission>

                <Button onClick={cancelCreation} variant="outlined" color="secondary" label="Cancel" />
              </ActionButtons>
            </>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AddPatient;
